import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { useHasStaffPermission } from '@/auth/RequireStaffPermission';
import { StaffPermissions } from '@/auth/staffPermissions';
import {
  DiagnosticLevels,
  DiagnosticRefusalError,
  DiagnosticScopes,
  messagesFor,
  unplacedMessages,
  useRaiseDiagnostics,
  type DiagnosticLevelName,
  type DiagnosticScopeName,
} from '@/routes/diagnostics/useDiagnostics';

const field =
  'h-9 w-full rounded-md border border-[var(--color-input)] bg-transparent px-2 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none';

/**
 * Raising an override (M1-34).
 *
 * Two of this story's acceptance criteria are about what this dialog must
 * **not** do, and both are easy to violate by being helpful:
 *
 * 1. ⚠️ **The TTL is not clamped here.** No `max` on the input, no ceiling
 *    constant, no pre-check. The server refuses anything over its limit and
 *    names both the limit and what was asked for; clamping in the browser would
 *    mean that refusal never happens and someone believes diagnostics are on for
 *    a week. The console does not even know the number — it learns it from the
 *    refusal, which is the only place it is authoritative.
 *
 * 2. ⚠️ **Hiding the platform-wide scopes is not the enforcement.** They are
 *    hidden without `staff:diagnostics.global`, because the console must not
 *    imply someone can do more than they can — but the submit path still
 *    renders the API's 403 if one comes back. That is reachable in practice:
 *    `/admin/me` is cached for five minutes on both sides, so an enrolment
 *    changed mid-session leaves a console showing controls the API has already
 *    stopped honouring.
 */
export function RaiseDiagnosticsDialog({
  initialTarget,
  onClose,
  onRaised,
}: {
  /** Prefilled when arriving from a tenant's page. */
  initialTarget?: string;
  onClose: () => void;
  /** Raw facts up; the page owns the live region and does the `t()` call (M1-30). */
  onRaised: (scope: DiagnosticScopeName, target: string | null) => void;
}) {
  const { t } = useTranslation();
  const hasPermission = useHasStaffPermission();
  const canScopePlatformWide = hasPermission(StaffPermissions.diagnosticsGlobal);

  const scopes = canScopePlatformWide
    ? DiagnosticScopes
    : DiagnosticScopes.filter((s) => s === 'Account');

  const [scope, setScope] = useState<DiagnosticScopeName>('Account');
  const [target, setTarget] = useState(initialTarget ?? '');
  const [level, setLevel] = useState<DiagnosticLevelName>('Debug');
  const [hours, setHours] = useState('');
  const [reason, setReason] = useState('');

  const raise = useRaiseDiagnostics();
  const refusal = raise.error instanceof DiagnosticRefusalError ? raise.error.refusal : undefined;

  const needsTarget = scope !== 'Global';
  // Only the things the form itself knows. The reason is required by the API
  // too, and its refusal is still rendered — asking for it here is a courtesy,
  // not a second enforcement point.
  const canSubmit = reason.trim() !== '' && (!needsTarget || target.trim() !== '');

  function submit() {
    raise.mutate(
      {
        scope,
        target: needsTarget ? target.trim() : null,
        // Blank means "let the server pick". Sending 0 instead would be a
        // different request and the server refuses it ("must be positive").
        hours: hours.trim() === '' ? null : Number(hours),
        level,
        reason: reason.trim(),
      },
      {
        onSuccess: () => {
          onRaised(scope, needsTarget ? target.trim() : null);
          onClose();
        },
      },
    );
  }

  return (
    <Dialog
      titleId="raise-diagnostics-title"
      title={t('diagnostics.raise.title')}
      description={t('diagnostics.raise.description')}
      onClose={onClose}
      testId="raise-diagnostics-dialog"
      dismissLabel={t('diagnostics.raise.dismiss')}
      footer={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" size="sm" disabled={!canSubmit || raise.isPending} onClick={submit}>
            {raise.isPending ? t('diagnostics.raise.saving') : t('diagnostics.raise.confirm')}
          </Button>
        </>
      }
    >
      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('diagnostics.raise.scope')}</span>
        <select
          className={field}
          value={scope}
          onChange={(event) => setScope(event.target.value as DiagnosticScopeName)}
        >
          {scopes.map((option) => (
            <option key={option} value={option}>
              {t(`diagnostics.scope.${option}`)}
            </option>
          ))}
        </select>
        <FieldErrors messages={messagesFor(refusal, 'Scope')} />
      </label>

      {/* Said plainly rather than left as an unexplained short list. "The
          console must not imply they can do more than they can" cuts both
          ways: a technician who sees only one option and no reason will
          reasonably assume the feature is broken. */}
      {!canScopePlatformWide ? (
        <p className="text-xs text-[var(--color-muted-foreground)]">
          {t('diagnostics.raise.accountScopeOnly')}
        </p>
      ) : null}

      {needsTarget ? (
        <label className="block text-sm">
          <span className="mb-1 block font-medium">
            {t(scope === 'Account' ? 'diagnostics.raise.accountId' : 'diagnostics.raise.category')}
          </span>
          <input
            required
            className={field}
            value={target}
            placeholder={t(
              scope === 'Account'
                ? 'diagnostics.raise.accountIdPlaceholder'
                : 'diagnostics.raise.categoryPlaceholder',
            )}
            onChange={(event) => setTarget(event.target.value)}
          />
        </label>
      ) : null}

      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('diagnostics.raise.level')}</span>
        <select
          className={field}
          value={level}
          onChange={(event) => setLevel(event.target.value as DiagnosticLevelName)}
        >
          {DiagnosticLevels.map((option) => (
            <option key={option} value={option}>
              {t(`diagnostics.level.${option}`)}
            </option>
          ))}
        </select>
        <FieldErrors messages={messagesFor(refusal, 'Level')} />
      </label>

      <div className="text-sm">
        {/* `htmlFor` + `aria-describedby`, not a wrapping label: the hint below
            would otherwise become part of the field's accessible name. */}
        <label htmlFor="diagnostics-hours" className="mb-1 block font-medium">
          {t('diagnostics.raise.hours')}
        </label>
        <input
          id="diagnostics-hours"
          type="number"
          min="0"
          step="0.5"
          aria-describedby="diagnostics-hours-hint"
          className={field}
          value={hours}
          placeholder={t('diagnostics.raise.hoursPlaceholder')}
          onChange={(event) => setHours(event.target.value)}
        />
        {/* ⚠️ No `max`, and the ceiling is not named. Naming it would mean
            hardcoding a server-configurable number (`Diagnostics:MaximumTtl`)
            that could drift into a lie; a `max` would stop the request and the
            refusal with it. The refusal itself names the real limit. */}
        <span
          id="diagnostics-hours-hint"
          className="mt-1 block text-xs text-[var(--color-muted-foreground)]"
        >
          {t('diagnostics.raise.hoursHint')}
        </span>
      </div>

      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('diagnostics.raise.reason')}</span>
        <input
          required
          className={field}
          value={reason}
          placeholder={t('diagnostics.raise.reasonPlaceholder')}
          onChange={(event) => setReason(event.target.value)}
        />
      </label>

      {refusal?.kind === 'forbidden' ? (
        // Our sentence, not the API's — `TypedResults.Forbid()` sends no body,
        // so there is nothing to quote. It says what happened and who can do it,
        // rather than "something went wrong".
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {t('diagnostics.raise.forbidden')}
        </p>
      ) : null}

      {/* The API's exact words. The TTL ceiling arrives here, naming both the
          limit and the number requested, which is the whole reason not to
          paraphrase it. */}
      <FieldErrors messages={unplacedMessages(refusal)} />

      {refusal?.kind === 'unknown' ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {t('diagnostics.raise.failed', { status: refusal.status })}
        </p>
      ) : null}
    </Dialog>
  );
}

function FieldErrors({ messages }: { messages: string[] }) {
  if (messages.length === 0) {
    return null;
  }

  return (
    <>
      {messages.map((message) => (
        <p key={message} role="alert" className="mt-1 text-sm text-[var(--color-destructive)]">
          {message}
        </p>
      ))}
    </>
  );
}
